import React from "react";
import {
  DocLayout,
  DocBreadcrumb,
  DocTitle,
  DocAlert,
  DocLink,
  DocNavFooter,
  DocCard,
} from "@/components/docs";
import { ApiEndpoint, CodeBlock, Parameter } from "@/components/docs/api";
import { docsColors, docsComponents } from "@/lib/docs-theme";
import {
  GlobeAltIcon,
  WifiIcon,
  CommandLineIcon,
  CheckCircleIcon,
} from "@heroicons/react/24/outline";

export default function ApiMonitorsPage() {
  const breadcrumbs = [
    { label: "Documentation", href: "/docs" },
    { label: "API Reference", href: "/docs/api" },
    { label: "Monitors API" },
  ];

  return (
    <DocLayout
      title="Monitors API - Uptime Monitor Documentation"
      description="Complete API reference for managing uptime monitors programmatically"
      canonical="https://uptime-monitor.io/docs/api/monitors"
    >
      <DocBreadcrumb items={breadcrumbs} />

      <DocTitle
        title="Monitors API"
        description="Create, update, pause, and delete monitors programmatically."
      />

      <DocAlert variant="info" className="mb-8">
        <p>
          Monitors define what endpoints or services to check for uptime. Each monitor has a
          specific type (HTTPS, TCP, Ping) determined by the settings object structure, and can be
          deployed across multiple global regions for comprehensive monitoring coverage.
        </p>
        <p className="mt-2 text-sm">
          <strong>Note:</strong> The monitor type is automatically determined from your settings
          object (e.g., <code>&ldquo;https&rdquo;</code> settings create HTTPS monitors,{" "}
          <code>&ldquo;tcp&rdquo;</code> settings create TCP monitors).
        </p>
        <p className="mt-2 text-sm">
          <strong>Dashboard vs API Defaults:</strong> The web dashboard uses different defaults for
          better user experience (e.g., fail_threshold=2, timeout=10). The API uses the values
          documented here.
        </p>
      </DocAlert>

      {/* Quick Reference */}
      <section className="mb-12">
        <h2 className={`text-2xl font-semibold ${docsColors.text.heading} mb-4`}>Monitor Types</h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <DocCard className="p-4 text-center">
            <GlobeAltIcon className="h-8 w-8 text-blue-500 mx-auto mb-2" />
            <div className={`font-semibold ${docsColors.text.heading}`}>HTTPS/HTTP</div>
            <div className={`text-sm ${docsColors.text.muted}`}>Website monitoring</div>
          </DocCard>

          <DocCard className="p-4 text-center">
            <CommandLineIcon className="h-8 w-8 text-blue-500 mx-auto mb-2" />
            <div className={`font-semibold ${docsColors.text.heading}`}>TCP</div>
            <div className={`text-sm ${docsColors.text.muted}`}>Port connectivity</div>
          </DocCard>

          <DocCard className="p-4 text-center">
            <WifiIcon className="h-8 w-8 text-blue-500 mx-auto mb-2" />
            <div className={`font-semibold ${docsColors.text.heading}`}>Ping</div>
            <div className={`text-sm ${docsColors.text.muted}`}>ICMP monitoring</div>
          </DocCard>
        </div>

        <p className={`${docsColors.text.body}`}>
          All monitor types can be deployed across <strong>AWS regions on 6 continents</strong> for
          comprehensive coverage and reliability testing from multiple geographic locations.
        </p>
      </section>

      {/* List Monitors */}
      <ApiEndpoint
        method="GET"
        path="/api/monitors"
        description="Retrieve a paginated list of monitors with optional filtering and sorting."
        queryParameters={[
          {
            name: "page",
            type: "integer",
            required: false,
            description: "Page number for pagination",
            default: "1",
            example: "2",
          },
          {
            name: "per_page",
            type: "integer",
            required: false,
            description: "Items per page (max 100)",
            default: "50",
            example: "25",
          },
          {
            name: "sort",
            type: "string",
            required: false,
            description: "Sort field: name, created_at, updated_at",
            default: "created_at",
            example: "name",
          },
          {
            name: "order",
            type: "string",
            required: false,
            description: "Sort order: asc, desc",
            default: "desc",
            example: "asc",
          },
          {
            name: "active",
            type: "boolean",
            required: false,
            description: "Filter by monitor status",
            example: "true",
          },
          {
            name: "monitor_type",
            type: "string",
            required: false,
            description: "Filter by monitor type: https, tcp, ping",
            example: "https",
          },
        ]}
        requestExample={`curl -X GET "https://api.uptime-monitor.io/api/monitors?page=1&per_page=20&sort=name&order=asc" \\
  -H "Authorization: Bearer um_xxxx"`}
        responseExample={`{
  "status": "ok",
  "data": {
    "monitors": [
      {
        "id": "507f1f77bcf86cd799439011",
        "name": "🌐 My Website",
        "active": true,
        "check_interval": 300,
        "timeout": 30,
        "fail_threshold": 3,
        "regions": ["us-east-1", "eu-west-1"],
        "settings": {
          "https": {
            "url": "https://example.com",
            "check_certificate_expiration": true
          }
        },
        "contacts": ["507f1f77bcf86cd799439012"],
        "last_status": "up",
        "last_status_change": 1640995800,
        "created_at": 1640995200
      }
    ],
    "pagination": {
      "page": 1,
      "per_page": 20,
      "total": 45,
      "total_pages": 3,
      "has_next": true,
      "has_prev": false
    }
  }
}`}
      />

      {/* Get Monitor */}
      <ApiEndpoint
        method="GET"
        path="/api/monitors/:id"
        description="Retrieve detailed information about a specific monitor."
        parameters={[
          {
            name: "id",
            type: "string",
            required: true,
            description: "Monitor ID",
            example: "507f1f77bcf86cd799439011",
          },
        ]}
        requestExample={`curl -X GET https://api.uptime-monitor.io/api/monitors/507f1f77bcf86cd799439011 \\
  -H "Authorization: Bearer um_xxxx"`}
        responseExample={`{
  "status": "ok",
  "data": {
    "id": "507f1f77bcf86cd799439011",
    "name": "🌐 My Website",
    "active": true,
    "check_interval": 300,
    "timeout": 30,
    "fail_threshold": 3,
    "regions": ["us-east-1", "eu-west-1"],
    "settings": {
      "https": {
        "url": "https://example.com",
        "check_certificate_expiration": true
      }
    },
    "contacts": ["507f1f77bcf86cd799439012"],
    "last_status": "up",
    "last_status_change": 1640995800,
    "created_at": 1640995200
  }
}`}
        errorResponses={[
          {
            status: 404,
            code: "MONITOR_NOT_FOUND",
            description: "Monitor with the specified ID does not exist",
          },
        ]}
      />

      {/* Create Monitor */}
      <ApiEndpoint
        method="POST"
        path="/api/monitors"
        description="Create a new monitor to track website or service uptime."
        bodyParameters={[
          {
            name: "name",
            type: "string",
            required: true,
            description: "Monitor display name (max 100 characters)",
            example: "🌐 My Website",
          },
          {
            name: "active",
            type: "boolean",
            required: false,
            description: "Monitor status",
            default: "true",
          },
          {
            name: "check_interval",
            type: "integer",
            required: false,
            description: "Check interval in seconds (10-600)",
            default: "300",
            example: "120",
          },
          {
            name: "timeout",
            type: "integer",
            required: false,
            description: "Check timeout in seconds (5-60)",
            default: "30",
            example: "15",
          },
          {
            name: "fail_threshold",
            type: "integer",
            required: false,
            description: "Failed checks before marking as down (1-10)",
            default: "2",
            example: "3",
          },
          {
            name: "regions",
            type: "array",
            required: false,
            description:
              "Monitoring regions. Must specify at least one region from the available regions listed below",
            default: "[]",
            example: '["us-east-1", "eu-west-1"]',
          },
          {
            name: "settings",
            type: "object",
            required: true,
            description: "Monitor type-specific configuration",
          },
          {
            name: "contacts",
            type: "array",
            required: false,
            description: "Array of contact IDs for notifications",
            example: '["507f1f77bcf86cd799439012"]',
          },
        ]}
        requestExample={`curl -X POST https://api.uptime-monitor.io/api/monitors \\
  -H "Authorization: Bearer um_xxxx" \\
  -H "Content-Type: application/json" \\
  -d '{
    "name": "🌐 My Website",
    "check_interval": 300,
    "timeout": 30,
    "fail_threshold": 2,
    "regions": ["us-east-1", "eu-west-1"],
    "settings": {
      "https": {
        "url": "https://example.com",
        "check_certificate_expiration": true
      }
    },
    "contacts": ["507f1f77bcf86cd799439012"]
  }'`}
        requestExampleLanguage="bash"
        responseExample={`{
  "status": "ok",
  "data": {
    "id": "507f1f77bcf86cd799439011",
    "name": "🌐 My Website",
    "active": true,
    "check_interval": 300,
    "timeout": 30,
    "fail_threshold": 2,
    "regions": ["us-east-1", "eu-west-1"],
    "settings": {
      "https": {
        "url": "https://example.com",
        "check_certificate_expiration": true
      }
    },
    "contacts": ["507f1f77bcf86cd799439012"],
    "last_status": "up",
    "last_status_change": 1640995800,
    "created_at": 1640995200
  }
}`}
        errorResponses={[
          {
            status: 422,
            code: "VALIDATION_ERROR",
            description: "Required fields are missing or invalid",
          },
        ]}
      />

      {/* Monitor Types Examples */}
      <section className="mb-12">
        <h2 className={`text-2xl font-semibold ${docsColors.text.heading} mb-6`}>
          Monitor Configuration Examples
        </h2>

        <p className={`${docsColors.text.body} mb-6`}>
          Each monitor type requires specific configuration in the <code>settings</code> object.
          Here are examples for each supported monitor type:
        </p>

        <div className="space-y-8">
          {/* HTTPS Monitor */}
          <DocCard className="p-6">
            <h3 className={`text-xl font-semibold ${docsColors.text.heading} mb-3`}>
              <GlobeAltIcon className="inline h-5 w-5 mr-2" />
              HTTPS/HTTP Monitor
            </h3>
            <p className={`${docsColors.text.body} mb-4`}>
              Monitor website availability and optionally check SSL certificate expiration.
            </p>
            <CodeBlock language="json" title="HTTPS Monitor Configuration">
              {`{
  "name": "🌐 My Website",
  "check_interval": 300,
  "timeout": 30,
  "fail_threshold": 2,
  "regions": ["us-east-1", "eu-west-1"],
  "settings": {
    "https": {
      "url": "https://example.com",
      "check_certificate_expiration": true
    }
  },
  "contacts": ["507f1f77bcf86cd799439012"]
}`}
            </CodeBlock>
          </DocCard>

          {/* TCP Monitor */}
          <DocCard className="p-6">
            <h3 className={`text-xl font-semibold ${docsColors.text.heading} mb-3`}>
              <CommandLineIcon className="inline h-5 w-5 mr-2" />
              TCP Monitor
            </h3>
            <p className={`${docsColors.text.body} mb-4`}>
              Monitor TCP port connectivity for databases, APIs, and other services.
            </p>
            <CodeBlock language="json" title="TCP Monitor Configuration">
              {`{
  "name": "🔌 Database Server",
  "check_interval": 120,
  "timeout": 15,
  "fail_threshold": 3,
  "regions": ["us-east-1"],
  "settings": {
    "tcp": {
      "url": "tcp://db.example.com:5432"
    }
  },
  "contacts": ["507f1f77bcf86cd799439012"]
}`}
            </CodeBlock>
          </DocCard>

          {/* Ping Monitor */}
          <DocCard className="p-6">
            <h3 className={`text-xl font-semibold ${docsColors.text.heading} mb-3`}>
              <WifiIcon className="inline h-5 w-5 mr-2" />
              Ping Monitor
            </h3>
            <p className={`${docsColors.text.body} mb-4`}>
              Monitor server connectivity using ICMP ping packets.
            </p>
            <CodeBlock language="json" title="Ping Monitor Configuration">
              {`{
  "name": "🏓 Server Connectivity",
  "check_interval": 60,
  "timeout": 10,
  "fail_threshold": 5,
  "regions": ["us-east-1", "eu-west-1", "ap-southeast-1"],
  "settings": {
    "ping": {
      "url": "ping://8.8.8.8"
    }
  },
  "contacts": ["507f1f77bcf86cd799439012"]
}`}
            </CodeBlock>
          </DocCard>
        </div>
      </section>

      {/* Update Monitor */}
      <ApiEndpoint
        method="PUT"
        path="/api/monitors/:id"
        description="Update an existing monitor's configuration. Only provide fields you want to change."
        parameters={[
          {
            name: "id",
            type: "string",
            required: true,
            description: "Monitor ID",
            example: "507f1f77bcf86cd799439011",
          },
        ]}
        bodyParameters={[
          {
            name: "name",
            type: "string",
            required: false,
            description: "Updated monitor display name",
            example: "🌐 Updated Website Name",
          },
          {
            name: "active",
            type: "boolean",
            required: false,
            description: "Monitor status",
          },
          {
            name: "check_interval",
            type: "integer",
            required: false,
            description: "Check interval in seconds (10-600)",
            example: "120",
          },
          {
            name: "timeout",
            type: "integer",
            required: false,
            description: "Check timeout in seconds (5-60)",
          },
          {
            name: "fail_threshold",
            type: "integer",
            required: false,
            description: "Failed checks before marking as down (1-10)",
          },
          {
            name: "regions",
            type: "array",
            required: false,
            description: "Monitoring regions. See available regions below",
          },
          {
            name: "settings",
            type: "object",
            required: false,
            description: "Monitor type-specific configuration",
          },
          {
            name: "contacts",
            type: "array",
            required: false,
            description: "Array of contact IDs for notifications",
          },
        ]}
        requestExample={`curl -X PUT https://api.uptime-monitor.io/api/monitors/507f1f77bcf86cd799439011 \\
  -H "Authorization: Bearer um_xxxx" \\
  -H "Content-Type: application/json" \\
  -d '{
    "name": "🌐 Updated Website Name",
    "check_interval": 120,
    "contacts": ["507f1f77bcf86cd799439012", "507f1f77bcf86cd799439013"]
  }'`}
        requestExampleLanguage="bash"
        responseExample={`{
  "status": "ok",
  "data": {
    "id": "507f1f77bcf86cd799439011",
    "name": "🌐 Updated Website Name",
    "active": true,
    "check_interval": 120,
    "timeout": 30,
    "fail_threshold": 2,
    "regions": ["us-east-1", "eu-west-1"],
    "settings": {
      "https": {
        "url": "https://example.com",
        "check_certificate_expiration": true
      }
    },
    "contacts": ["507f1f77bcf86cd799439012", "507f1f77bcf86cd799439013"],
    "last_status": "up",
    "last_status_change": 1640995800,
    "created_at": 1640995200
  }
}`}
        errorResponses={[
          {
            status: 404,
            code: "MONITOR_NOT_FOUND",
            description: "Monitor with the specified ID does not exist",
          },
          {
            status: 422,
            code: "VALIDATION_ERROR",
            description: "Invalid field values provided",
          },
        ]}
      />

      {/* Delete Monitor */}
      <ApiEndpoint
        method="DELETE"
        path="/api/monitors/:id"
        description="Permanently delete a monitor and all its historical data. This action cannot be undone."
        parameters={[
          {
            name: "id",
            type: "string",
            required: true,
            description: "Monitor ID",
            example: "507f1f77bcf86cd799439011",
          },
        ]}
        requestExample={`curl -X DELETE https://api.uptime-monitor.io/api/monitors/507f1f77bcf86cd799439011 \\
  -H "Authorization: Bearer um_xxxx"`}
        responseExample="HTTP 204 No Content"
        errorResponses={[
          {
            status: 404,
            code: "MONITOR_NOT_FOUND",
            description: "Monitor with the specified ID does not exist",
          },
        ]}
      />

      <DocAlert variant="warning" className="mb-8">
        <h3 className="font-semibold mb-2">Deletion Warning</h3>
        <p>
          Deleting a monitor permanently removes all historical data, statistics, and incident
          records. This action cannot be undone. Consider pausing the monitor instead if you only
          need to temporarily stop notifications while continuing to collect monitoring data.
        </p>
      </DocAlert>

      {/* Pause Monitor */}
      <ApiEndpoint
        method="POST"
        path="/api/monitors/:id/pause"
        description="Pause alerting for a monitor. Monitoring continues but no notifications will be sent."
        parameters={[
          {
            name: "id",
            type: "string",
            required: true,
            description: "Monitor ID",
            example: "507f1f77bcf86cd799439011",
          },
        ]}
        requestExample={`curl -X POST https://api.uptime-monitor.io/api/monitors/507f1f77bcf86cd799439011/pause \\
  -H "Authorization: Bearer um_xxxx"`}
        responseExample={`{
  "status": "ok",
  "data": {
    "id": "507f1f77bcf86cd799439011",
    "name": "🌐 My Website",
    "active": false,
    "updated_at": 1640995800
  }
}`}
        errorResponses={[
          {
            status: 404,
            code: "MONITOR_NOT_FOUND",
            description: "Monitor with the specified ID does not exist",
          },
        ]}
      />

      {/* Resume Monitor */}
      <ApiEndpoint
        method="POST"
        path="/api/monitors/:id/resume"
        description="Resume alerting for a paused monitor. Notifications will be sent again."
        parameters={[
          {
            name: "id",
            type: "string",
            required: true,
            description: "Monitor ID",
            example: "507f1f77bcf86cd799439011",
          },
        ]}
        requestExample={`curl -X POST https://api.uptime-monitor.io/api/monitors/507f1f77bcf86cd799439011/resume \\
  -H "Authorization: Bearer um_xxxx"`}
        responseExample={`{
  "status": "ok",
  "data": {
    "id": "507f1f77bcf86cd799439011",
    "name": "🌐 My Website",
    "active": true,
    "updated_at": 1640995800
  }
}`}
        errorResponses={[
          {
            status: 402,
            code: "SUBSCRIPTION_NOT_ACTIVE",
            description: "The account's subscription is not active",
          },
          {
            status: 404,
            code: "MONITOR_NOT_FOUND",
            description: "Monitor with the specified ID does not exist",
          },
        ]}
      />

      {/* Available Regions */}
      <section className="mb-12">
        <h2 className={`text-2xl font-semibold ${docsColors.text.heading} mb-6`}>
          Available Regions
        </h2>

        <p className={`${docsColors.text.body} mb-6`}>
          Monitors can be deployed across the following AWS regions where our checker infrastructure
          is deployed. Using multiple regions provides better coverage, redundancy, and helps
          identify region-specific connectivity issues.
        </p>

        <p className={`${docsColors.text.body} mb-6`}>
          <strong>Tip:</strong> You can get the current list of supported regions and their checker
          IP addresses at{" "}
          <a
            href="https://uptime-monitor.io/web_api/checker_ip_addresses"
            className={`${docsColors.link.primary} underline`}
            target="_blank"
            rel="noopener noreferrer"
          >
            https://uptime-monitor.io/web_api/checker_ip_addresses
          </a>
          . This is useful for whitelisting our monitoring infrastructure in your firewall rules.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <DocCard className="p-4">
            <h4 className={`font-semibold ${docsColors.text.heading} mb-2`}>North America</h4>
            <ul className={`text-sm ${docsColors.text.muted} space-y-1`}>
              <li>us-east-1 (N. Virginia)</li>
              <li>us-east-2 (Ohio)</li>
              <li>us-west-1 (N. California)</li>
              <li>us-west-2 (Oregon)</li>
            </ul>
          </DocCard>

          <DocCard className="p-4">
            <h4 className={`font-semibold ${docsColors.text.heading} mb-2`}>Europe</h4>
            <ul className={`text-sm ${docsColors.text.muted} space-y-1`}>
              <li>eu-west-1 (Ireland)</li>
              <li>eu-central-1 (Frankfurt)</li>
              <li>eu-central-2 (Zurich)</li>
              <li>eu-north-1 (Stockholm)</li>
            </ul>
          </DocCard>

          <DocCard className="p-4">
            <h4 className={`font-semibold ${docsColors.text.heading} mb-2`}>Asia Pacific</h4>
            <ul className={`text-sm ${docsColors.text.muted} space-y-1`}>
              <li>ap-east-1 (Hong Kong)</li>
              <li>ap-northeast-1 (Tokyo)</li>
              <li>ap-northeast-2 (Seoul)</li>
              <li>ap-southeast-1 (Singapore)</li>
              <li>ap-southeast-2 (Sydney)</li>
            </ul>
          </DocCard>

          <DocCard className="p-4">
            <h4 className={`font-semibold ${docsColors.text.heading} mb-2`}>Other Regions</h4>
            <ul className={`text-sm ${docsColors.text.muted} space-y-1`}>
              <li>sa-east-1 (São Paulo)</li>
              <li>af-south-1 (Cape Town)</li>
            </ul>
          </DocCard>
        </div>
      </section>

      <DocNavFooter
        prev={{ href: "/docs/api/contacts", text: "Contacts API" }}
        next={{ href: "/docs/api/incidents", text: "Incidents API" }}
        className="mt-12"
      />
    </DocLayout>
  );
}
